import React, { createContext, useMemo, useContext, useCallback, useState } from 'react'
import clsx from 'clsx'
import QrScanner from 'qr-scanner'
import { makeStyles } from '@material-ui/core/styles'
import { useDropzone } from 'react-dropzone'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import MuiButton from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import FormField from '../../default/form/FormField'
import FormSubmitButton from '../../default/form/FormSubmitButton'
import { FormPropType, getValueFromChangeEvent, getValueFromCheckboxEvent, getValueFromValue, getCodeInputProps } from '../../../utils/default/FormHelper'
import { InitialFormFunc, useGetData } from '../../../utils/default/ComplexFlowHook'
import { createDefaultFormState, ValueGetter, FormValidation } from '../../../utils/default/FormHook'
import useT from '../../../i18ns/admin/useT'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import { DepositMethodType, ClientType, AgentType } from '@golden/gdk-admin'
import RequiredText from '../../default/form/RequiredText'
import FormStateProvider from '../../default/form/FormStateProvider'
import { createValidateNotEmpty, createValidateRange, createValidateMin, createValidateMax, createErrorResult, createCorrectResult } from '../../../utils/default/Validator'
import depositTypeName from '../../../constants/default/depositTypeName'
import NumberInput from '../../default/form/NumberInput'
import OnOffCheckbox from '../../default/form/OnOffCheckbox'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import useGDK from '../../../providers/admin/gdk/useGDK'
import clientTypeName from '../../../constants/admin/clientTypeName'
import PlayerLayerInput, { PropTypes as PlayerLayerInputProps, PlayerLayerValue } from '../PlayerLayerInput'
import CheckboxGroup, { PropTypes as CheckboxGroupProps } from '../../default/form/CheckboxGroup'
import ClearIcon from '@material-ui/icons/Clear'
import DropDown, { PropTypes as DropDownProps } from '../../default/form/DropDown'

const useStyles = makeStyles(() => ({
  icon: {
    fontSize: 48
  },
  input: {
    display: 'none'
  },
  dragSection: {
    cursor: 'pointer'
  },
  label: {
    fontWeight: 600
  },
  imageContainer: {
    position: 'relative'
  },
  clear: {
    position: 'absolute',
    top: '0',
    right: '0',
    cursor: 'pointer',
    color: '#fff',
    padding: '1px',
    background: '#fb9678'
  }
}))

export interface EditFinancePiggyAlipayFormType {
  name: string
  account: string
  group: number | '--'
  min: string
  max: string
  receiver: string
  memo: string
  isActive: boolean
  clients: ClientType[]
  agentLayers: AgentType[]
  layer: PlayerLayerValue
  code: string
  qr: string
}

export const initialForm: InitialFormFunc<EditFinancePiggyAlipayFormType> = (defaultForm) => ({
  name: '',
  account: '',
  group: '--',
  min: '',
  max: '',
  receiver: '',
  memo: '',
  isActive: false,
  clients: [],
  layer: {
    layers: [],
    isForProxyDeposit: false
  },
  agentLayers: [],
  code: '',
  qr: '',
  ...defaultForm
})

const getValueFromEvent: ValueGetter<EditFinancePiggyAlipayFormType> = {
  name: getValueFromChangeEvent,
  account: getValueFromChangeEvent,
  group: getValueFromChangeEvent,
  min: getValueFromChangeEvent,
  max: getValueFromChangeEvent,
  receiver: getValueFromChangeEvent,
  memo: getValueFromChangeEvent,
  isActive: getValueFromCheckboxEvent,
  code: getValueFromChangeEvent,
  clients: getValueFromValue,
  agentLayers: getValueFromValue,
  layer: getValueFromValue,
  qr: getValueFromValue
}

interface PropTypes extends FormPropType<EditFinancePiggyAlipayFormType> {
  disableName?: boolean
  disableAccount?: boolean
  okText: string
  onBack: () => void
}

const memoFormHelperTextProps = { error: true }

const moneyInputProps = {
  thousandSeparator: true,
  decimalScale: 0,
  allowNegative: false,
  fixedDecimalScale: true,
  maxLegnth: 9
}

const codeInputProps = getCodeInputProps()

const FormContext = createContext(createDefaultFormState(initialForm()))

const TextField = React.memo(MuiTextField)

const Button = React.memo(MuiButton)

const FileUploader: React.FC = React.memo(() => {
  const classes = useStyles()
  const gdk = useGDK()
  const commonClasses = useCommonStyles()
  const globalDialog = useGlobalDialog()
  const { value, dispatch } = useContext(FormContext)
  const { t } = useT()
  const handleFileUpload = useCallback((file: File | undefined) => {
    if (file) {
      QrScanner.scanImage(file)
        .then((result) => {
          dispatch({ type: 'change', value: result, label: 'qr' })
        })
        .catch((error) => {
          console.error(error)
          globalDialog.setConfig(createGlobalDialogConfig({
            showIcon: true,
            variant: 'error',
            showCancel: false,
            message: t('common.qrcodeInvalidate')
          }))
          globalDialog.setOpen({
            id: 'qrCodeError',
            isOK: false,
            value: true
          })
        })
    }
  }, [])
  const handleDrop = useCallback((files) => {
    const file = files[0]
    handleFileUpload(file)
  }, [handleFileUpload])
  const handleUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    handleFileUpload(file)
  }, [handleFileUpload])
  const { getRootProps, getInputProps } = useDropzone({ onDrop: handleDrop })
  return (
    <div className={clsx(commonClasses.grayFrameContainer, classes.dragSection)} {...getRootProps()}>
      <input
        type="file"
        accept=".png,.jpg,.jpeg"
        className={classes.input}
        onChange={handleUpload}
        {...getInputProps()}
      />
      {value.qr && value.qr !== ''
        ? (
        <div className={classes.imageContainer}>
          <ClearIcon className={classes.clear} onClick={(event) => {
            dispatch({ type: 'change', value: '', label: 'qr' })
            event.stopPropagation()
          }}/>
          <img src={gdk.qrcode.generateURL(value.qr, 200)} alt="" width="150" height="150" />
        </div>
          )
        : (
        <Grid container direction="column" justifyContent="center" alignItems="center">
          <Grid item>
            <CloudUploadIcon className={classes.icon} />
          </Grid>
          <Grid item>
            <p>{t('common.dropOrClick')} <span className={commonClasses.anchor}>{t('common.chooseFile')}</span> {t('common.uploadQRCode')}</p>
          </Grid>
        </Grid>
          )}
    </div>
  )
})

const EditFinancePiggyAlipayForm: React.FC<PropTypes> = (props) => {
  const {
    defaultValue,
    onSubmit,
    onBack,
    okText,
    disableAccount,
    disableName
  } = props
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const { t } = useT()
  const gdk = useGDK()

  const [groups, setGroups] = useState<Array<{
    id: number
    name: string
    cash_min: string
    cash_max: string
  }>>([])
  useGetData({
    gdkFunc: () => gdk.finance.getDepositWayGroupList({ method: DepositMethodType.SCAN_ALIPAY, page: 1 }),
    gdkFuncDependencies: [gdk],
    onSuccess: (res) => {
      setGroups(res.data.map((item) => ({
        id: item.id,
        name: item.name,
        cash_min: item.cash_min,
        cash_max: item.cash_max
      })))
    }
  })
  const groupOptions = useMemo(() => {
    return [
      {
        name: t('placeholder.notChooseYet'),
        value: '--'
      }
    ].concat(groups.map((el) => ({
      name: `${el.name}[${el.cash_min} - ${el.cash_max}]`,
      value: (el.id).toString()
    })))
  }, [t, groups])

  const validation = useMemo(() => {
    return {
      name: [
        {
          func: createValidateNotEmpty('name', t),
          when: ['blur', 'beforeClickSubmit']
        }
      ],
      account: [
        {
          func: createValidateNotEmpty('account', t),
          when: ['change', 'beforeClickSubmit']
        }
      ],
      group: [],
      min: [
        {
          func: createValidateNotEmpty('min', t),
          when: ['change', 'beforeClickSubmit']
        },
        {
          func: createValidateRange('min', (value) => Number(value as string), 'moreEqual', 1, t('error.mustMoreThanZero')),
          when: ['change', 'beforeClickSubmit']
        },
        {
          func: createValidateMin('min', 'max', t),
          when: ['change', 'beforeClickSubmit']
        },
        {
          func: (value, form, lastSubmitForm) => {
            if (value !== lastSubmitForm.min && (form.group && form.group !== '--')) {
              return { isPass: true, stop: false, newError: { group: t('error.pleaseCheckWayGroupRange') } }
            } else {
              return { isPass: true, stop: false, newError: { group: null } }
            }
          },
          when: ['blur']
        }
      ],
      max: [
        {
          func: createValidateNotEmpty('max', t),
          when: ['change', 'beforeClickSubmit']
        },
        {
          func: createValidateMax('min', 'max', t),
          when: ['change', 'beforeClickSubmit']
        },
        {
          func: (value, form, lastSubmitForm) => {
            if (value !== lastSubmitForm.max && (form.group && form.group !== '--')) {
              return { isPass: true, stop: false, newError: { group: t('error.pleaseCheckWayGroupRange') } }
            } else {
              return { isPass: true, stop: false, newError: { group: null } }
            }
          },
          when: ['blur']
        }
      ],
      receiver: [],
      memo: [],
      isActive: [],
      code: [
        {
          func: createValidateNotEmpty('code', t),
          when: ['change', 'beforeClickSubmit']
        }
      ],
      qr: [],
      clients: [
        {
          func: createValidateRange('clients', (value) => (value as any[]).length, 'moreThan', 0, t('error.mustNotEmpty')),
          when: ['beforeClickSubmit']
        }
      ],
      agentLayers: [
        // {
        //   func: createValidateRange('clients', (value) => (value as any[]).length, 'moreThan', 0, t('error.mustNotEmpty')),
        //   when: ['beforeClickSubmit']
        // }
      ],
      layer: [
        {
          func: (value) => {
            const layer = (value as PlayerLayerValue)
            if (layer.layers.length === 0 && !layer.isForProxyDeposit) return createErrorResult('layer', t('error.mustNotEmpty'))
            return createCorrectResult('layer')
          },
          when: ['beforeClickSubmit']
        }
      ],
      isForProxyDeposit: []
    } as FormValidation<EditFinancePiggyAlipayFormType>
  }, [t])

  const clientOptions = useMemo(() => {
    return Object.keys(clientTypeName)
      .map((key) => Number(key) as ClientType)
      .map((key) => ({ name: t(clientTypeName[key]), value: key }))
  }, [t])

  const memoInputProps = useMemo(() => ({
    classes: { input: commonClasses.memoField },
    inputProps: {
      placeholder: t('placeholder.inputMemo')
    }
  }), [commonClasses.memoField, t])

  return (
    <FormStateProvider
      context={FormContext}
      defaultValue={defaultValue}
      onSubmit={onSubmit}
      getValueFromEvent={getValueFromEvent}
      validation={validation}
    >
      <Grid container direction="column" spacing={3}>
        <Grid item>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={12} md={3}>
              <FormField<EditFinancePiggyAlipayFormType, TextFieldProps>
                context={FormContext}
                component={TextField}
                name="name"
                fullWidth
                required
                label={t('common.alipayName')}
                disabled={disableName}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t('common.depositWay')}
                value={t(depositTypeName[DepositMethodType.SCAN_ALIPAY])}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormField<EditFinancePiggyAlipayFormType, TextFieldProps>
                context={FormContext}
                component={TextField}
                name="account"
                fullWidth
                required
                label={t('common.alipayAccount')}
                placeholder={t('placeholder.inputAlipayAccount')}
                disabled={disableAccount}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={12} md={3}>
              <FormField<EditFinancePiggyAlipayFormType, TextFieldProps>
                context={FormContext}
                component={NumberInput}
                name="min"
                fullWidth
                required
                label={t('common.minDepositMoney')}
                inputProps={moneyInputProps}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormField<EditFinancePiggyAlipayFormType, TextFieldProps>
                context={FormContext}
                component={NumberInput}
                name="max"
                fullWidth
                required
                label={t('common.maxDepositMoney')}
                inputProps={moneyInputProps}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <FormField<EditFinancePiggyAlipayFormType, DropDownProps>
                context={FormContext}
                component={DropDown}
                name="group"
                fullWidth
                label={t('page.depositWayGroup')}
                options={groupOptions}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormField<EditFinancePiggyAlipayFormType, TextFieldProps>
                context={FormContext}
                component={TextField}
                name="receiver"
                fullWidth
                label={t('common.playerSideReceiver')}
                placeholder={t('placeholder.inputPlayerSideReceiver')}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Typography className={classes.label}>{t('common.memoForPlayer')}</Typography>
          <FormField<EditFinancePiggyAlipayFormType, TextFieldProps>
            context={FormContext}
            component={TextField}
            name="memo"
            multiline
            fullWidth
            type="text"
            margin="normal"
            variant="outlined"
            InputProps={memoInputProps}
            FormHelperTextProps={memoFormHelperTextProps}
          />
        </Grid>
        <Grid item>
          <FormField<EditFinancePiggyAlipayFormType, CheckboxGroupProps>
            context={FormContext}
            component={CheckboxGroup}
            name="clients"
            label={t('common.platformVersion')}
            options={clientOptions}
            required
          />
        </Grid>
        <Grid item>
          <FormField
            component={PlayerLayerInput}
            context={FormContext}
            name="layer"
            label={t('common.playerLayer')}
            required
            proxyDepositDisabled={false}
            isShowProxyDeposit={true}
          />
        </Grid>
        {/* <AgentLayerInput /> */}
        <Grid item>
          <Typography className={commonClasses.bold}>{t('common.status')}</Typography>
          <FormField<EditFinancePiggyAlipayFormType, PlayerLayerInputProps>
            component={OnOffCheckbox}
            context={FormContext}
            name="isActive"
            label={t('common.isStartAlipay')}
            isShowProxyDeposit={false}
          />
        </Grid>
        <Grid item>
          <Box
            paddingY={1.25}
            paddingX={2}
            className={commonClasses.pinkTitleBar}
          >
            <Typography variant="h5">
              {t('common.personAlipay')}
            </Typography>
          </Box>
        </Grid>
        <Grid item>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={12} md={3}>
              <FormField<EditFinancePiggyAlipayFormType, TextFieldProps>
                context={FormContext}
                component={NumberInput}
                name="code"
                fullWidth
                required
                inputProps={codeInputProps}
                label={t('common.googleCode')}
                placeholder={t('placeholder.inputGoogleCode2')}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <FileUploader />
        </Grid>
        <Grid item>
          <Box whiteSpace="pre">
            <Typography color="error">{t('common.fileSizeTip')}</Typography>
          </Box>
        </Grid>
        <Grid item>
          <RequiredText />
        </Grid>
        <Grid item>
          <Grid container direction="row" justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button
                className={commonClasses.greyButton}
                onClick={onBack}
              >
                {t('common.cancel')}
              </Button>
            </Grid>
            <Grid item>
              <FormSubmitButton
                component={Button}
                context={FormContext}
                className={commonClasses.purpleGradualButton}
                type="submit"
              >
                {okText}
              </FormSubmitButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </FormStateProvider>
  )
}

export default React.memo(EditFinancePiggyAlipayForm)
